import compareAsc from 'date-fns/compareAsc';
import format from 'date-fns/format';
import { PERCENT_OF_ALERTS } from 'utils/charts/constants';
import BlackThickLineIcon from 'assets/images/black-thick-line.svg';
import QuartzBlueThickLineIcon from 'assets/images/quartz-blue-thick-line.svg';
import { generateTrendAnalysisYAxisMin } from '../chart';

const tooltip = (data) => {
    return {
        formatter: function (params) {
            const { name: date, value, seriesIndex } = params;
            const category =
                seriesIndex === 0
                    ? 'Concentration Risk'
                    : 'Portfolio Risk Mismatch';
            const filteredData =
                seriesIndex === 0 && data
                    ? data.find(
                          (item) =>
                              item.creationDate === date &&
                              item.percentConcentrationRisk ===
                                  parseFloat(value)
                      )
                    : data.find(
                          (item) =>
                              item.creationDate === date &&
                              item.percentPortfolioRisk === parseFloat(value)
                      );
            const noOfAlerts =
                seriesIndex === 0 && filteredData
                    ? filteredData.totalConcentrationRisk
                    : filteredData.totalPortfolioRisk;
            return (
                '<span style="line-height: 30px;">Breach Category</span>' +
                '<span style="font-weight: 400; margin-left: 8px; margin-bottom: 5px; color: #929292;">' +
                category +
                '</span>' +
                '<br><span style="line-height: 30px;">Creation Date</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                date +
                '</span>' +
                '<br><span style="line-height: 30px;">% of Alert</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                value +
                '%' +
                '</span>' +
                '<br><span style="line-height: 30px;"># of Alert</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                noOfAlerts +
                '</span>'
            );
        },
        textStyle: {
            color: '#000000',
            fontWeight: 700,
            fontSize: '10px'
        },
        extraCssText:
            'box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08); ' +
            'border: 1px solid white; ' +
            'border-radius: 16px; ' +
            'padding: 16px;'
    };
};

const portfolioRiskMismatchGraphic = {
    image: {
        type: 'image',
        style: {
            image: BlackThickLineIcon
        },
        cursor: 'default',
        bottom: '3.5%',
        top: '95.5%',
        left: '23%'
    },
    text: {
        type: 'text',
        style: {
            text: 'Portfolio Risk Mismatch Alert',
            font: '12px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '26.5%'
    }
};

const concentrationRiskGraphic = {
    image: {
        type: 'image',
        style: {
            image: QuartzBlueThickLineIcon
        },
        cursor: 'default',
        top: '95.5%',
        bottom: '3.5%',
        left: '53%'
    },
    text: {
        type: 'text',
        style: {
            text: 'Concentration Risk Alert',
            font: '12px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '56.5%'
    }
};

export const scenarioTwoTrendAnalysisChartOptions = (trendData) => {
    const formattedData = trendData
        ? [...trendData]
              .filter((data) => data.month !== 0 || data.year !== 0)
              .sort((a, b) =>
                  compareAsc(
                      new Date(a.year, a.month - 1),
                      new Date(b.year, b.month - 1)
                  )
              )
              .reduce(
                  (acc, curr) => [
                      ...acc,
                      {
                          ...curr,
                          creationDate: format(
                              new Date(curr.year, curr.month - 1),
                              'MMM-yy'
                          )
                      }
                  ],
                  []
              )
        : null;
    const concentrationValues = formattedData
        ? formattedData.reduce(
              (acc, curr) => [...acc, curr.percentConcentrationRisk.toFixed(2)],
              []
          )
        : [];
    const portfolioValues = formattedData
        ? formattedData.reduce(
              (acc, curr) => [...acc, curr.percentPortfolioRisk.toFixed(2)],
              []
          )
        : [];
    const calculateOffset = (value, valueToCompare) => {
        if (value === 0 && valueToCompare === 0) return 0;
        const isValueTheSmallerNumber =
            Math.min(value, valueToCompare) === value;
        const percentageDiff =
            (Math.abs(value - valueToCompare) /
                Math.max(value, valueToCompare)) *
            100;
        if (percentageDiff < 13) {
            return isValueTheSmallerNumber ? 30 : 0;
        } else {
            return 0;
        }
    };

    return {
        title: [
            {
                left: 'left',
                text: 'Trend Analysis',
                textStyle: {
                    color: '#244B60',
                    fontSize: '12px'
                }
            }
        ],
        tooltip: tooltip(formattedData),
        xAxis: [
            {
                data: formattedData
                    ? formattedData.reduce(
                          (acc, curr) => [...acc, curr.creationDate],
                          []
                      )
                    : [],
                axisTick: {
                    show: false
                },
                axisLine: {
                    show: false
                },
                splitLine: {
                    show: false
                },
                axisLabel: {
                    show: true,
                    padding: [16, 0, 0, 0]
                }
            }
        ],
        yAxis: [
            {
                name: PERCENT_OF_ALERTS,
                nameLocation: 'center',
                nameTextStyle: {
                    color: '#7FADBF',
                    fontWeight: 600,
                    fontSize: 12
                },
                nameGap: 60,
                axisLabel: {
                    show: false
                },
                axisLine: {
                    show: false
                },
                splitLine: {
                    show: false
                },
                min: generateTrendAnalysisYAxisMin([
                    ...concentrationValues,
                    ...portfolioValues
                ])
            }
        ],
        series: [
            {
                type: 'line',
                symbolSize: [7, 7],
                data: formattedData
                    ? concentrationValues.map((value, idx) => {
                          const cValue = parseFloat(value);
                          const pValue = parseFloat(portfolioValues[idx]);
                          return {
                              value,
                              label: {
                                  show: true,
                                  position: 'top',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  lineheight: 14,
                                  offset: [0, calculateOffset(cValue, pValue)],
                                  formatter: '{c}%',
                                  color: '#549AB5'
                              }
                          };
                      })
                    : [],
                lineStyle: {
                    color: '#549AB5'
                },
                itemStyle: {
                    color: '#549AB5'
                },
                emphasis: {
                    focus: 'series'
                }
            },
            {
                type: 'line',
                symbolSize: [7, 7],
                data: formattedData
                    ? portfolioValues.map((value, idx) => {
                          const pValue = parseFloat(value);
                          const cValue = parseFloat(concentrationValues[idx]);
                          return {
                              value,
                              label: {
                                  show: true,
                                  position: 'top',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  lineheight: 14,
                                  offset: [0, calculateOffset(pValue, cValue)],
                                  formatter: '{c}%'
                              }
                          };
                      })
                    : [],
                lineStyle: {
                    color: 'black'
                },
                itemStyle: {
                    color: 'black'
                },
                emphasis: {
                    focus: 'series'
                }
            }
        ],
        grid: {
            width: '400px',
            height: '240px',
            top: '90px',
            left: '15%'
        },
        graphic: [
            portfolioRiskMismatchGraphic.image,
            portfolioRiskMismatchGraphic.text,
            concentrationRiskGraphic.image,
            concentrationRiskGraphic.text
        ]
    };
};
